import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import { type JourneyManager } from '../hooks/useJourney';
import { buildSession, getTokenRemainingMs } from './initSession';
import { type AppAction } from './appState';
import { type RonanInteractionContext } from '../types';

/**
 * redirectInit — the redirect (non-embedded) init flow.
 *
 * When Ronan is loaded with `oauth_interaction_start_uri` in the query string,
 * it parses the OAuth interaction from the URL, reads the interaction context,
 * and builds the session. The graphql token's expiry is watched with a timer
 * that aborts the flow with a `timeout` error.
 *
 * This is a plain function (not a hook): the caller owns the `useEffect` and
 * uses the returned cleanup to cancel the expiry timer.
 */
export function redirectInit(
  dispatch: React.Dispatch<AppAction>,
  journey: JourneyManager,
  isMobile: boolean,
): (() => void) | void {
  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.has('oauth_interaction_start_uri')) return;

  let tokenExpiryTimeout: ReturnType<typeof setTimeout> | null = null;

  const init = async () => {
    try {
      const url = new URL(window.location.toString());
      const interaction = new YonduOAuthInteraction(url);
      journey.setOAuthInteraction(interaction);
      const ctx = await interaction.start<RonanInteractionContext>();

      // Watch the graphql token's expiry: reject immediately if it has
      // already passed, otherwise schedule a timeout error.
      const graphqlToken = ctx.request.graphqlToken;
      if (graphqlToken) {
        const remainingMs = getTokenRemainingMs(graphqlToken);
        if (remainingMs !== null) {
          if (remainingMs <= 0) {
            dispatch({ action: 'INIT_ERROR', variant: 'timeout' });
            return;
          }
          tokenExpiryTimeout = setTimeout(() => {
            dispatch({ action: 'INIT_ERROR', variant: 'timeout' });
          }, remainingMs);
        }
      }

      const result = await buildSession(ctx);
      if (result.status === 'aborted') return;
      if (result.status === 'error') {
        dispatch({ action: 'INIT_ERROR', variant: result.variant });
        return;
      }

      const { session } = result;
      dispatch({
        action: 'INIT_SUCCESS',
        session,
        autoSelectedToken:
          session.tokens.length === 1 ? session.tokens[0] : null,
        isMobile,
      });
    } catch (e) {
      const isNetwork =
        e instanceof TypeError && e.message === 'Failed to fetch';
      dispatch({
        action: 'INIT_ERROR',
        variant: isNetwork ? 'networkLoss' : 'techFail',
      });
    }
  };

  init();

  return () => {
    if (tokenExpiryTimeout) clearTimeout(tokenExpiryTimeout);
  };
}
